import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.75rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.gray};
`;

const LastUpdated = styled.p`
  font-style: italic;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.grayLight};
`;

const TermsOfServicePage: React.FC = () => {
  return (
    <PageContainer>
      <PageTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Terms of Service
      </PageTitle>
      
      <LastUpdated>Last Updated: July 8, 2025</LastUpdated>
      
      <Section>
        <Paragraph>
          Welcome to FylePrep. Please read these Terms of Service ("Terms") carefully as they contain important information about your legal rights, remedies, and obligations. By accessing or using the FylePrep platform, you agree to comply with and be bound by these Terms.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Paragraph>
          By accessing or using our service, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the service.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>2. Description of Service</SectionTitle>
        <Paragraph>
          FylePrep provides a platform for accounting professionals to streamline tax preparation workflows through document collection, organization, and preparation tools. The service includes features for client communication, document management, and workflow automation.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>3. User Accounts</SectionTitle>
        <Paragraph>
          When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding the password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>4. User Content</SectionTitle>
        <Paragraph>
          Our service allows you to upload, submit, store, send, and receive content. You retain ownership of any intellectual property rights that you hold in that content. By uploading content to our service, you grant FylePrep a worldwide, royalty-free license to use, host, store, reproduce, modify, create derivative works, communicate, publish, publicly perform, and distribute such content.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>5. Prohibited Activities</SectionTitle>
        <Paragraph>
          You agree not to engage in any of the following prohibited activities:
        </Paragraph>
        <List>
          <ListItem>Using the service for any illegal purpose or in violation of any laws</ListItem>
          <ListItem>Violating or infringing other people's intellectual property, privacy, or other rights</ListItem>
          <ListItem>Attempting to interfere with, compromise the system integrity or security, or decipher any transmissions to or from the servers running the service</ListItem>
          <ListItem>Uploading invalid data, viruses, worms, or other software agents through the service</ListItem>
          <ListItem>Using the service in any manner that could disable, overburden, damage, or impair the site</ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>6. Payment Terms</SectionTitle>
        <Paragraph>
          Some aspects of the service may be provided for a fee. You agree to pay all fees associated with the services you select. We may change our fees at any time by posting the changes on the site or by notifying you directly.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>7. Termination</SectionTitle>
        <Paragraph>
          We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including without limitation if you breach the Terms. Upon termination, your right to use the service will immediately cease.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>8. Limitation of Liability</SectionTitle>
        <Paragraph>
          In no event shall FylePrep, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the service.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>9. Changes to Terms</SectionTitle>
        <Paragraph>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>10. Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about these Terms, please contact us at: legal@fyleprep.com
        </Paragraph>
      </Section>
    </PageContainer>
  );
};

export default TermsOfServicePage;
